import { useEffect } from 'react';
import { useTheme } from '@mui/material';
import {
    DateAndTimeSelectedEvent,
    EventScheduledEvent,
    EventTypeViewedEvent,
    PopupModal,
    ProfilePageViewedEvent,
    useCalendlyEventListener,
} from 'react-calendly';
import type { Utm } from 'react-calendly/typings/calendly';

import './calendlySSC.css';

interface CalendlyDialogProps {
    onClose: () => void;
    open: boolean;
    url: string;
    memberUuid?: string;
    prefill?: {
        name: string;
        email: string;
        company?: string;
        comments?: string;
        [key: string]: any;
    };
    pageSettings?: {
        hideEventTypeDetails?: boolean;
        queryString?: {
            utm_source?: string;
            [key: string]: any;
        };
    };
    utm?: Utm;
    onProfilePageViewed?: (e: ProfilePageViewedEvent) => void;
    onDateAndTimeSelected?: (e: DateAndTimeSelectedEvent) => void;
    onEventTypeViewed?: (e: EventTypeViewedEvent) => void;
    onEventScheduled?: (e: EventScheduledEvent) => void;
    hideDetails?: boolean;
}

function CalendlyDialogSSC({
    onClose,
    open,
    url,
    memberUuid,
    prefill,
    pageSettings = {},
    onProfilePageViewed,
    onDateAndTimeSelected,
    onEventTypeViewed,
    onEventScheduled,
    utm,
    hideDetails = true,
    ...props
}: CalendlyDialogProps) {
    const { palette } = useTheme();

    useCalendlyEventListener({
        onProfilePageViewed,
        onDateAndTimeSelected,
        onEventTypeViewed,
        onEventScheduled,
    });

    const fullUrl = `${url}${url.includes('?') ? '&' : '?'}${pageSettings.queryString || ''}`;

    useEffect(() => {
        const rootElement = document.getElementById('root');
        if (rootElement) {
            if (open) {
                rootElement.classList.add('calendly-dialog-ssc');
            } else {
                rootElement.classList.remove('calendly-dialog-ssc');
            }
        }

        return () => {
            rootElement?.classList.remove('calendly-dialog-ssc');
        };
    }, [open]);

    return (
        <PopupModal
            url={fullUrl}
            open={open}
            onModalClose={onClose}
            utm={{
                ...utm,
                ...(memberUuid ? { utmContent: `member:${memberUuid}` } : {}),
            }}
            pageSettings={{
                hideEventTypeDetails: hideDetails,
                ...pageSettings,
                backgroundColor: palette.background.default.replace('#', ''),
                textColor: palette.text.primary.replace('#', ''),
                primaryColor: palette?.red?.[500]?.replace('#', ''),
            }}
            prefill={{
                ...prefill,
            }}
            rootElement={document.getElementById('root') as HTMLElement}
            {...props}
        />
    );
}

export default CalendlyDialogSSC;
